import {
  useState, useEffect, useCallback, useRef,
} from 'react';

const POLL_INTERVAL = 10000; // 10s when there are active clips

const useEventClips = (deviceId, from, to, statusFilter) => {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const fetchClips = useCallback(async () => {
    if (!deviceId) {
      setClips([]);
      return;
    }

    const params = new URLSearchParams();
    params.append('deviceId', deviceId);
    if (from) params.append('from', from.toISOString());
    if (to) params.append('to', to.toISOString());
    if (statusFilter) params.append('status', statusFilter);

    try {
      setLoading(true);
      const response = await fetch(`/api/events/clips?${params.toString()}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setClips(data);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [deviceId, from, to, statusFilter]);

  // Auto-poll when there are PENDING or RECORDING clips
  useEffect(() => {
    const hasActive = clips.some(
      (c) => c.status === 'pending' || c.status === 'recording',
    );

    if (hasActive) {
      pollRef.current = setInterval(fetchClips, POLL_INTERVAL);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [clips, fetchClips]);

  // Initial fetch when params change
  useEffect(() => {
    fetchClips();
  }, [fetchClips]);

  const retryClip = useCallback(async (clipId) => {
    const response = await fetch(`/api/events/clips/${clipId}/retry`, { method: 'POST' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    await fetchClips();
  }, [fetchClips]);

  const deleteClip = useCallback(async (clipId) => {
    const response = await fetch(`/api/events/clips/${clipId}`, { method: 'DELETE' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    await fetchClips();
  }, [fetchClips]);

  const requestClip = useCallback(async (eventId, channel = 1) => {
    const response = await fetch(`/api/events/${eventId}/clip?channel=${channel}`, { method: 'POST' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    await fetchClips();
    return response.json();
  }, [fetchClips]);

  return {
    clips,
    loading,
    error,
    refresh: fetchClips,
    retryClip,
    deleteClip,
    requestClip,
  };
};

export default useEventClips;
